// @flow
/** @jsx isolateComponent */

import { Fragment } from 'react';

import { isolateComponent } from '@zapier/common-utils';
import {
  type AccountPlan,
  type TrialExtensionReason,
  trialExtensionReasons,
} from '@zapier/billing/src/types';
import { Button, Link, Spacer } from '@zapier/design-system';

import ContentWrapper from '../ContentWrapper';
import NudgeImage from '../NudgeImage';

const reasonMessagingProps = ({ appName, canExtendTrial, plan }) => ({
  [trialExtensionReasons.premium]: {
    bodyText: canExtendTrial ? (
      <Fragment>
        To turn on your Zap, you'll need access to{' '}
        <Link href="/apps/categories/premium" target="_blank">
          Premium Apps
        </Link>
        . Upgrade now or start a free 7-day trial of the Professional plan to
        use unlimited Premium Apps.
      </Fragment>
    ) : (
      <Fragment>
        To turn on your Zap, you'll need access to{' '}
        <Link href="/apps/categories/premium" target="_blank">
          Premium Apps
        </Link>
        . Edit your Zap or upgrade now to turn on your Zap.
      </Fragment>
    ),
    headingText: 'Premium App',
    image: (
      <NudgeImage appName={appName} type={trialExtensionReasons.premium} />
    ),
    subtitleText: 'Premium Feature',
  },
  [trialExtensionReasons.premiumLimit]: {
    bodyText: canExtendTrial ? (
      <Fragment>
        To turn on your Zap, you'll need more{' '}
        <Link href="/apps/categories/premium" target="_blank">
          Premium Apps
        </Link>
        . Upgrade now or start a free 7-day trial of the Professional plan to
        use unlimited Premium Apps.
      </Fragment>
    ) : (
      <Fragment>
        To turn on your Zap, you'll need access to unlimited{' '}
        <Link href="/apps/categories/premium" target="_blank">
          Premium Apps
        </Link>
        . Edit your Zap or upgrade now to turn on your Zap.
      </Fragment>
    ),
    headingText: "You've run out of Premium Apps",
    image: (
      <NudgeImage appName={appName} type={trialExtensionReasons.premiumLimit} />
    ),
    subtitleText: 'Premium Feature',
  },
  [trialExtensionReasons.zapLimit]: {
    bodyText: canExtendTrial
      ? "To turn on your Zap, you'll need access to more Zaps. Upgrade now or start a free 7-day trial of the Professional plan to use unlimited Zaps."
      : "To turn on your Zap, you'll need access to more Zaps. Turn off some of your other Zaps or upgrade now to turn on this Zap.",
    headingText: 'More Zaps',
    image: <NudgeImage type={trialExtensionReasons.zapLimit} />,
    subtitleText: 'Heads Up',
  },
  [trialExtensionReasons.multistep]: {
    bodyText: canExtendTrial
      ? `To create a Zap with more than one action, you'll need a paid plan. Upgrade now or start a free 7-day trial of the Professional plan to use multi-step Zaps.`
      : "To create a Zap with more than one action, you'll need a paid plan. Edit your zap or upgrade now to turn on your Zap.",
    headingText: 'Multi-step Zap',
    image: <NudgeImage type={trialExtensionReasons.multistep} />,
    subtitleText: 'Premium Feature',
  },
  [trialExtensionReasons.paths]: {
    bodyText: canExtendTrial ? (
      <Fragment>
        To turn on your Zap, you'll need access to{' '}
        <Link
          href="/help/create/customize/add-branching-logic-to-zaps-with-paths"
          target="_blank"
        >
          Paths
        </Link>
        . Upgrade now or start a free 7-day trial of the Professional plan to
        use premium features like Paths.
      </Fragment>
    ) : (
      <Fragment>
        To turn on your Zap, you'll need access to{' '}
        <Link
          href="/help/create/customize/add-branching-logic-to-zaps-with-paths"
          target="_blank"
        >
          Paths
        </Link>
        . Edit your Zap or upgrade now to turn on your Zap.
      </Fragment>
    ),
    headingText: 'Paths',
    image: <NudgeImage type={trialExtensionReasons.paths} />,
    subtitleText: 'Premium Feature',
  },
  [trialExtensionReasons.taskLimit]: {
    bodyText: canExtendTrial
      ? `To activate your Zap, you'll need more Tasks. Upgrade now or start a free 7-day trial of Professional for ${Number(
          plan.limits.action_limit
        ).toLocaleString()} tasks.`
      : "To activate your Zap, you'll need more Tasks. Reduce the number of Zaps you're using or upgrade now to activate.",
    headingText: 'More Tasks',
    image: <NudgeImage type={trialExtensionReasons.taskLimit} />,
    subtitleText: 'Premium Feature',
  },
  [trialExtensionReasons.unknown]: {
    bodyText: "To activate your Zap, you'll need to upgrade your account.",
    headingText: 'Upgrade Needed',
  },
});

type ReasonProps = {|
  appName?: string,
  canExtendTrial: boolean,
  onClose: () => void,
  onExtendTrial: () => void,
  onUpgradeClick: () => void,
  plan: AccountPlan,
  reason: TrialExtensionReason,
|};

const Reason = ({
  appName,
  canExtendTrial,
  onClose,
  onExtendTrial,
  onUpgradeClick,
  plan,
  reason,
}: ReasonProps) => {
  const messagingProps = reasonMessagingProps({
    appName,
    canExtendTrial,
    plan,
  })[reason || trialExtensionReasons.unknown];
  const contentProps = {
    ...messagingProps,
    buttons: [
      <Button
        casing="uppercase"
        hasShadow={true}
        key="upgrade"
        onClick={() => onUpgradeClick()}
      >
        Upgrade Now
      </Button>,
      <Spacer key="space" width={10} />,
      canExtendTrial ? (
        <Button
          casing="uppercase"
          color="greyAlt"
          key="extend"
          onClick={onExtendTrial}
        >
          Start Trial
        </Button>
      ) : (
        <Button
          casing="uppercase"
          color="greyAlt"
          key="close"
          onClick={onClose}
        >
          Back
        </Button>
      ),
    ],
    subtitleText: reason ? messagingProps.subtitleText : null,
  };
  return <ContentWrapper {...contentProps} />;
};

export default Reason;
